// 📄 hooks/useProjectStats.ts
// Estadísticas del proyecto para el panel de "Soluciones del Proyecto":
// reutiliza las mismas fuentes que el stepper de Integración (Commerce
// Automation) — commerceIntegration.environments.DEV.checklistProgress —
// y la colección de incidentes/interfacesZ ya existentes.
//
// Nota: el checklist de Firestore solo guarda un booleano por paso
// (completado / no completado), no hay un estado intermedio "en progreso"
// persistido — por eso el panel trabaja con 2 estados (completado /
// pendiente) en vez de los 3 del mockup original.
import { useEffect, useState } from 'react';
import { collection, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { getGeneralsConfig } from '../services/commerce/integrationService.ts';
import { getTabSteps, TAB_LABELS } from '../pages/views/AribaCommerce/Integracion/useGeneralConfig.ts';
import { getIncidentes } from '../services/incidenteService.ts';

export interface TabProgress {
  tab: string;
  completados: number;
  total: number;
}

export interface ProjectStats {
  loading: boolean;
  error: string | null;
  totalPasos: number;
  pasosCompletados: number;
  pasosPendientes: number;
  porcentaje: number;
  srCount: number;
  issuesAbiertosCount: number;
  interfacesCount: number;
  avancePorTab: TabProgress[];
}

const EMPTY_STATS: Omit<ProjectStats, 'loading' | 'error'> = {
  totalPasos: 0,
  pasosCompletados: 0,
  pasosPendientes: 0,
  porcentaje: 0,
  srCount: 0,
  issuesAbiertosCount: 0,
  interfacesCount: 0,
  avancePorTab: []
};

export function useProjectStats(projectId: string, erpTipo?: string): ProjectStats {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState(EMPTY_STATS);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    let cancelado = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [commerceIntegration, incidentes, interfacesCountSnap] = await Promise.all([
          getGeneralsConfig(projectId),
          getIncidentes(projectId),
          getCountFromServer(collection(db, 'projects', projectId, 'interfacesZ'))
        ]);

        if (cancelado) return;

        const tabSteps = getTabSteps(erpTipo);
        const checklistProgress: Record<string, boolean> = commerceIntegration?.environments?.DEV?.checklistProgress || {};

        const avancePorTab: TabProgress[] = Object.keys(TAB_LABELS).map((tabKey) => {
          const steps: string[] = tabSteps[tabKey] || [];
          const completados = steps.filter((stepId) => checklistProgress[stepId] === true).length;
          return { tab: TAB_LABELS[tabKey], completados, total: steps.length };
        });

        const totalPasos = avancePorTab.reduce((sum, t) => sum + t.total, 0);
        const pasosCompletados = avancePorTab.reduce((sum, t) => sum + t.completados, 0);
        const pasosPendientes = totalPasos - pasosCompletados;
        const porcentaje = totalPasos > 0 ? Math.round((pasosCompletados / totalPasos) * 100) : 0;

        const srCount = incidentes.filter((i) => !!i.categoriaSR).length;
        const issuesAbiertosCount = incidentes.filter((i) => i.estado === 'Abierto').length;

        setData({
          totalPasos,
          pasosCompletados,
          pasosPendientes,
          porcentaje,
          srCount,
          issuesAbiertosCount,
          interfacesCount: interfacesCountSnap.data().count,
          avancePorTab
        });
      } catch (err) {
        console.error('Error cargando estadísticas del proyecto:', err);
        if (!cancelado) setError('No se pudieron cargar las estadísticas.');
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [projectId, erpTipo]);

  return { loading, error, ...data };
}
